import { useEffect, useRef, useState } from 'react'

import { motion, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'

// ─── Shared fade-in ───────────────────────────────────────────────────────────

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, ease: [0.25, 0, 0.1, 1], delay }}
    >
      {children}
    </motion.div>
  )
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

const SLIDES = [
  { src: '/images/optimized/IMG_5169.webp', sub: 'Sojha · Himachal Pradesh', title: 'A Forest\nRetreat' },
  { src: '/images/optimized/IMG_5118.webp', sub: '2,700 metres above sea level', title: 'Where Silence\nBegins' },
  { src: '/images/optimized/IMG_5113.webp', sub: 'Handcrafted in the oak canopy', title: 'Your Treehouse\nAwaits' },
]

function Hero() {
  const [cur, setCur] = useState(0)

  useEffect(() => {
    const t = setInterval(() => setCur(c => (c + 1) % SLIDES.length), 6000)
    return () => clearInterval(t)
  }, [])

  return (
    <section className="relative overflow-hidden bg-forest-950"
      style={{ height: 'calc(100vh - var(--nav-h))', minHeight: '520px' }}
    >
      {SLIDES.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1500 ${i === cur ? 'opacity-100' : 'opacity-0'}`}
          style={{ transitionDuration: '1500ms' }}
        >
          <img
                src={s.src}
                alt={s.title}
                className="h-full w-full object-cover"
                {...(i === 0 ? { fetchPriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement> : { loading: 'lazy' as const })}
              />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/55 to-ink/20" />
        </div>
      ))}

      {/* Content — left-aligned, Aman style */}
      <div className="relative z-10 h-full flex flex-col justify-end pb-24 wrap">
        <motion.p
          key={`sub-${cur}`}
          className="text-[11px] tracking-widest3 uppercase text-white/80 mb-5"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
        >
          {SLIDES[cur].sub}
        </motion.p>

        <motion.h1
          key={`h-${cur}`}
          className="text-5xl font-semibold text-white leading-tight lg:text-7xl"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.25, 0, 0.1, 1] }}
          style={{ whiteSpace: 'pre-line', textShadow: '0 2px 24px rgba(0,0,0,0.45)' }}
        >
          {SLIDES[cur].title}
        </motion.h1>

        <motion.div
          className="mt-10 flex items-center gap-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
        >
          <Link to="/inquire" className="btn-ghost-aman">
            Reserve
          </Link>
          <a
            href="#about"
            onClick={e => { e.preventDefault(); document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' }) }}
            className="text-[11px] tracking-widest2 uppercase text-white/80 hover:text-white transition-colors"
          >
            Discover ↓
          </a>
        </motion.div>

        {/* Slide dots */}
        <div className="absolute bottom-10 right-16 flex gap-3">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setCur(i)}
              className={`h-px transition-all duration-700 ${i === cur ? 'w-10 bg-white' : 'w-4 bg-white/30'}`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── About ────────────────────────────────────────────────────────────────────

const ABOUT_STATS = [
  { value: '2,700m', label: 'Altitude' },
  { value: '8', label: 'Accommodations' },
  { value: 'Solar', label: 'Powered' },
  { value: 'Local', label: 'Farm-to-Table' },
]

function About() {
  return (
    <section id="about" className="bg-[#1e3525] mt-20 lg:mt-28">
      <div className="grid lg:grid-cols-2 lg:min-h-[620px]">

        {/* Left — image mosaic, equal-height tiles on right column */}
        <div className="grid grid-cols-2 grid-rows-2 gap-0.5 h-[420px] lg:h-full">
          <img src="/images/optimized/IMG_5174.webp" alt="Scenic Solitude property" loading="lazy" className="row-span-2 h-full w-full object-cover" />
          <img src="/images/optimized/IMG_5109.webp" alt="Interior sitting area" loading="lazy" className="h-full w-full object-cover" />
          <img src="/images/optimized/IMG_5095.webp" alt="Interior with mountain view" loading="lazy" className="h-full w-full object-cover" />
        </div>

        {/* Right — text on dark */}
        <FadeIn className="flex flex-col justify-center px-10 py-20 lg:px-16 lg:py-24">
          <span className="text-[10px] tracking-[0.35em] uppercase text-white/50 font-semibold mb-6 block">Our Story</span>
          <h2 className="text-4xl lg:text-5xl font-semibold text-white leading-tight tracking-tight">
            Born from the Forest
          </h2>
          <p className="mt-8 text-white/75 text-base leading-[1.85]">
            Scenic Solitude sits 2,700 meters high above Sojha, where the forest meets the sky.
            It is a quiet space built around the peaceful rhythm of the mountains.
          </p>
          <p className="mt-4 text-white/75 text-base leading-[1.85]">
            Every room and treehouse is shaped by hand from local timber and stone to preserve the
            land. Here, life is intentionally slow. You can feel it in the mountain spring water
            heated by the sun, and taste it in daily meals made with ingredients from the valley.
          </p>

          {/* Stats row */}
          <div className="mt-12 grid grid-cols-4 gap-4 border-t border-white/15 pt-10">
            {ABOUT_STATS.map(s => (
              <div key={s.label}>
                <p className="text-2xl font-semibold text-white tracking-tight">{s.value}</p>
                <p className="text-[10px] tracking-[0.25em] uppercase text-white/50 mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          <Link to="/about" className="mt-10 btn-ghost-aman self-start">
            Our Story
          </Link>
        </FadeIn>

      </div>
    </section>
  )
}

// ─── Stays ────────────────────────────────────────────────────────────────────

const TREEHOUSES = [
  { src: '/images/thumbs/IMG_5164.webp', name: 'The Canopy Nest', loc: 'Single-Storey Treehouse', price: '₹8,500', desc: 'A private treehouse perched in the oak canopy. Uninterrupted valley views, a personal deck for sunrise, and the silence of the forest floor far below. Ideal for couples, honeymooners, and solo retreats.' },
  { src: '/images/thumbs/IMG_5113.webp', name: 'The Canopy Loft', loc: 'Duplex Family Treehouse', price: '₹13,500', desc: 'A two-storey treehouse suite with a mezzanine level, built for families and small groups. Four adults sleep comfortably; the upper level accommodates more. Panoramic ridge views at every window.' },
]

const CABINS = [
  { src: '/images/thumbs/IMG_5094.webp', name: 'Cabin 1', loc: 'Standard · Sleeps 2', price: '₹11,500', desc: 'A handcrafted wooden cabin set quietly among the trees. Warm, simple, and close to the earth — suited for couples and solo writers.' },
  { src: '/images/thumbs/IMG_5132.webp', name: 'Cabin 2', loc: 'Standard · Sleeps 2', price: '₹11,500', desc: 'A private retreat with forest views, built from local timber. Wake to birdsong and the smell of pine.' },
  { src: '/images/thumbs/IMG_5143.webp', name: 'Cabins 3, 4 & 5', loc: 'Standard · Sleeps 2', price: '₹11,500', desc: 'Three identical cabins tucked into the hillside — book one or all. Each is self-contained, serene, and steps from the shared forest path.' },
  { src: '/images/optimized/img_familysuite1.webp', name: 'Family Duplex Suite', loc: 'Family Duplex · Sleeps 6', price: '₹13,500', desc: 'The largest of the cabins — a duplex lodge with a mezzanine floor, designed for families and small co-working groups. Sleeps up to six.' },
]

function Treehouses() {
  return (
    <section id="treehouses" className="pt-24 pb-16 lg:pt-32 lg:pb-20">
      <div className="wrap">

        <FadeIn className="mb-20 flex items-end justify-between">
          <div>
            <span className="label-aman">Where You Stay</span>
            <h2 className="heading-aman">Treehouses<br />&amp; Wooden Cabins</h2>
          </div>
          <Link to="/inquire" className="btn-outline-aman text-ink hidden sm:inline-flex">
            Enquire
          </Link>
        </FadeIn>

        {/* Treehouses — horizontal cards, price prominent */}
        <FadeIn><p className="label-aman mb-8">Treehouses</p></FadeIn>
        <div className="flex flex-col gap-1 mb-20">
          {TREEHOUSES.map((t, i) => (
            <FadeIn key={t.name} delay={i * 0.1}>
              <div className="group grid md:grid-cols-[1fr_1.1fr] bg-cream-100 overflow-hidden border border-cream-300 hover:border-ink/20 transition-colors duration-300">
                {/* Image */}
                <div className="overflow-hidden">
                  <img src={t.src} alt={t.name} loading="lazy"
                    className="h-64 md:h-72 w-full object-cover group-hover:scale-[1.02] transition-transform duration-700" />
                </div>
                {/* Details */}
                <div className="flex flex-col justify-between p-8 lg:p-10">
                  <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-3">{t.loc}</p>
                    <h3 className="text-2xl font-semibold text-ink">{t.name}</h3>
                    <p className="mt-4 text-sm text-ink-soft leading-relaxed">{t.desc}</p>
                  </div>
                  <div className="flex items-end justify-between mt-8 pt-6 border-t border-cream-300">
                    <div>
                      <p className="text-[10px] tracking-[0.25em] uppercase text-ink-muted mb-1">From</p>
                      <p className="text-4xl font-semibold text-ink tracking-tight">{t.price}<span className="text-base font-normal text-ink-muted ml-1">/night</span></p>
                    </div>
                    <Link to={`/inquire?cabin=${t.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className="btn-outline-aman text-ink text-[10px]">
                      Book Now
                    </Link>
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>

{/* Wooden Cabins — grid with bold price */}
        <FadeIn>
          <p className="text-[11px] tracking-[0.3em] uppercase font-semibold text-ink mb-8">Wooden Cabins</p>
        </FadeIn>
        <div className="grid gap-1 md:grid-cols-2 lg:grid-cols-4 items-stretch">
          {CABINS.map((h, i) => (
            <FadeIn key={h.name} delay={i * 0.07} className="flex">
              <div className="group flex flex-col w-full bg-cream-100 border border-cream-300 hover:border-ink/20 transition-colors duration-300 overflow-hidden">
                <div className="overflow-hidden shrink-0">
                  <img src={h.src} alt={h.name} loading="lazy"
                    className="aspect-[4/3] w-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                </div>
                <div className="flex flex-col flex-1 p-5">
                  <p className="text-[9px] tracking-[0.3em] uppercase text-ink-muted mb-2">{h.loc}</p>
                  <h3 className="text-base font-semibold text-ink leading-snug">{h.name}</h3>
                  <p className="mt-2 text-xs text-ink-soft leading-relaxed flex-1 hidden lg:block">{h.desc}</p>
                  <div className="mt-auto pt-4 border-t border-cream-300 flex items-center justify-between">
                    <div>
                      <p className="text-[9px] tracking-[0.2em] uppercase text-ink-muted">From</p>
                      <p className="text-xl font-semibold text-ink mt-0.5">{h.price}<span className="text-xs font-normal text-ink-muted ml-1">/night</span></p>
                    </div>
                    <Link to={`/inquire?cabin=${h.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className="text-[9px] tracking-[0.2em] uppercase border border-ink/40 px-3 py-1.5 text-ink hover:bg-ink hover:text-cream-50 transition-all duration-200">
                      Book
                    </Link>
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* Footer CTA */}
        <FadeIn className="mt-16 text-center">
          <Link to="/treehouses" className="text-[11px] tracking-widest2 uppercase text-ink-muted hover:text-ink transition-colors">
            View All Rooms & Details →
          </Link>
        </FadeIn>

      </div>
    </section>
  )
}

// ─── Experiences ──────────────────────────────────────────────────────────────

const EXP = [
  { n: '01', title: 'Forest Walks', desc: 'Guided trails through oak and rhododendron with a local naturalist.' },
  { n: '02', title: 'Sunrise Viewing', desc: 'Watch the Himalayan horizon dissolve into gold from your private deck.' },
  { n: '03', title: 'Farm-to-Table', desc: 'Himachali cooking over wood fire. Seasonal. Everything within 20km.' },
  { n: '04', title: 'Stargazing', desc: 'Zero light pollution at altitude. The Milky Way is not a metaphor here.' },
  { n: '05', title: 'Library & Stillness', desc: 'Hundreds of books. A hammock in the canopy. No schedule required.' },
  { n: '06', title: 'Village Trails', desc: 'Walk to Sojha. Chai at a dhaba. The unhurried Himachal.' },
]

function Experiences() {
  return (
    <section id="experiences" className="py-16 lg:py-20 border-t border-cream-300">
      <div className="wrap">

        <FadeIn className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-10">
          <h2 className="text-xl font-semibold text-ink tracking-tight">At Scenic Solitude</h2>
          <p className="text-sm text-ink-soft font-light">More than a bed in the trees</p>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6">
            {EXP.map((e) => (
              <div key={e.n} className="flex items-start gap-3">
                <span className="text-[10px] tracking-widest text-ink-muted shrink-0 mt-[3px]">{e.n}</span>
                <div>
                  <p className="text-sm font-semibold text-ink">{e.title}</p>
                  <p className="text-xs text-ink-soft font-light leading-relaxed mt-0.5">{e.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </FadeIn>

      </div>
    </section>
  )
}

// ─── Gallery ─────────────────────────────────────────────────────────────────

const GALL = [
  { src: '/images/thumbs/IMG_5169.webp', tall: true },
  { src: '/images/thumbs/IMG_5174.webp', tall: false },
  { src: '/images/thumbs/IMG_5143.webp', tall: false },
  { src: '/images/thumbs/IMG_5111.webp', tall: true },
  { src: '/images/thumbs/IMG_5132.webp', tall: false },
  { src: '/images/thumbs/IMG_5147.webp', tall: false },
]

// ─── Restaurant ──────────────────────────────────────────────────────────────

function Restaurant() {
  return (
    <section id="restaurant" style={{ backgroundColor: '#2a1f14' }}>
      <div className="grid lg:grid-cols-2 lg:min-h-[580px]">

        {/* Left — image mosaic, equal tiles */}
        <div className="grid grid-cols-2 grid-rows-2 gap-0.5 h-[380px] lg:h-full">
          <img src="/images/optimized/IMG_5118.webp" alt="Forest balcony" loading="lazy" className="row-span-2 h-full w-full object-cover" />
          <img src="/images/optimized/IMG_5108.webp" alt="Dining view" loading="lazy" className="h-full w-full object-cover" />
          <img src="/images/optimized/IMG_5164.webp" alt="Nature dining" loading="lazy" className="h-full w-full object-cover" />
        </div>

        {/* Right — text on warm dark */}
        <FadeIn className="flex flex-col justify-center px-10 py-16 lg:px-16 lg:py-20">
          <span className="text-[10px] tracking-[0.35em] uppercase text-white/50 font-semibold mb-5 block">Dining</span>
          <h2 className="text-3xl lg:text-4xl font-semibold text-white leading-tight tracking-tight">
            A Restaurant Among the Trees
          </h2>
          <p className="mt-6 text-white/70 text-base leading-[1.85]">
            Our open-air restaurant sits at the edge of the forest, framed by oak branches and
            mountain sky. The menu follows the seasons — ingredients sourced from within the
            valley, cooked simply over wood fire.
          </p>
          <p className="mt-4 text-white/70 text-base leading-[1.85]">
            Breakfast at dawn. Long lunches. Dinners by lantern light. Open to guests and visitors alike.
          </p>

          {/* Details strip */}
          <div className="mt-8 flex gap-8 border-t border-white/10 pt-6">
            {[
              { label: 'Open', value: 'Daily 7am – 10pm' },
              { label: 'Cuisine', value: 'Himachali · Wood fire' },
            ].map(d => (
              <div key={d.label}>
                <p className="text-[9px] tracking-[0.25em] uppercase text-white/40 mb-1">{d.label}</p>
                <p className="text-sm text-white/80 font-light">{d.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/contact" className="btn-ghost-aman">
              Reserve a Table
            </Link>
            <a
              href={`https://wa.me/919418040013?text=${encodeURIComponent('Hi, I would like to reserve a table at the Scenic Solitude restaurant.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 text-[11px] tracking-widest2 uppercase text-white/60 hover:text-white transition-colors group"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#25D366] text-white shrink-0 group-hover:bg-[#1ebe5d] transition-colors">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </span>
              +91 94180 40013
            </a>
          </div>
        </FadeIn>

      </div>
    </section>
  )
}

// ─── Gallery ─────────────────────────────────────────────────────────────────

function Gallery() {
  return (
    <section id="gallery" className="py-32 lg:py-40" style={{ backgroundColor: '#1e3525' }}>
      <div className="wrap">

        <FadeIn className="mb-16 flex items-end justify-between">
          <div>
            <span className="label-aman text-white/50">Gallery</span>
            <h2 className="heading-aman text-white">The Place</h2>
          </div>
          <Link to="/gallery" className="btn-ghost-aman hidden sm:inline-flex">
            View All
          </Link>
        </FadeIn>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-1 auto-rows-[200px]">
          {GALL.map((g, i) => (
            <FadeIn
              key={i}
              delay={i * 0.06}
              className={g.tall ? 'row-span-2' : ''}
            >
              <img src={g.src} alt="Scenic Solitude" loading="lazy" className="h-full w-full object-cover" />
            </FadeIn>
          ))}
        </div>

      </div>
    </section>
  )
}

// ─── Testimonials ─────────────────────────────────────────────────────────────

interface Review { id: string; guest_name: string; city: string; quote: string; rating: number }

function Stars({ rating }: { rating: number }) {
  return <span className="text-sm" style={{ color: '#2d5a3d' }}>{'★'.repeat(rating)}{'☆'.repeat(5 - rating)}</span>
}

function Testimonials() {
  const [reviews, setReviews] = useState<Review[]>([])
  const BASE_URL = import.meta.env.VITE_API_BASE_URL as string

  const [total, setTotal] = useState(0)

  useEffect(() => {
    fetch(`${BASE_URL}/api/reviews`)
      .then(r => r.json())
      .then((d: { success: boolean; data: Review[] }) => {
        if (d.success) {
          setTotal(d.data.length)
          setReviews(d.data.slice(0, 3))
        }
      })
      .catch(() => {})
  }, [])

  if (reviews.length === 0) return null

  return (
    <section className="py-32 lg:py-40 bg-cream-200">
      <div className="wrap">

        <FadeIn className="mb-20 flex items-end justify-between">
          <div>
            <span className="label-aman">In Their Words</span>
          </div>
          {total > 0 && (
            <p className="text-[11px] tracking-widest2 uppercase text-ink-muted">
              {total} {total === 1 ? 'Review' : 'Reviews'}
            </p>
          )}
        </FadeIn>

        <div className="grid gap-16 md:grid-cols-3">
          {reviews.map((r, i) => (
            <FadeIn key={r.id} delay={i * 0.12}>
              <Stars rating={r.rating} />
              <p className="mt-3 text-xl font-semibold text-ink leading-relaxed">"{r.quote}"</p>
              <p className="mt-6 text-[11px] tracking-widest2 uppercase text-ink-muted">{r.guest_name} · {r.city}</p>
            </FadeIn>
          ))}
        </div>

      </div>
    </section>
  )
}

// ─── Location ─────────────────────────────────────────────────────────────────

function Location() {
  return (
    <section className="py-16 lg:py-20 border-t border-cream-300">
      <div className="wrap">

        {/* Header row */}
        <FadeIn className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-8">
          <div>
            <span className="label-aman mb-1">Getting Here</span>
            <h2 className="text-xl font-semibold text-ink tracking-tight">Finding Scenic Solitude, Sojha</h2>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://maps.app.goo.gl/dtcNRhkxs7jkZFGM7"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] tracking-widest2 uppercase text-ink-soft hover:text-ink transition-colors"
            >
              Open in Maps ↗
            </a>
            <Link to="/contact" className="text-[11px] tracking-widest2 uppercase border border-ink/30 px-4 py-2 text-ink hover:bg-ink hover:text-cream-50 transition-all duration-200">
              Plan Journey
            </Link>
          </div>
        </FadeIn>

        {/* Map — full width, colorful, compact */}
        <FadeIn delay={0.1} className="relative">
          <iframe
            title="Scenic Solitude location"
            src="https://maps.google.com/maps?q=31.566823,77.370206&t=&z=14&ie=UTF8&iwloc=&output=embed"
            className="w-full h-72 lg:h-96 border border-cream-300"
            loading="lazy"
          />
        </FadeIn>

        {/* Transport — compact 3-col row below map */}
        <FadeIn delay={0.15}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 mt-0 divide-y sm:divide-y-0 sm:divide-x divide-cream-300 border border-t-0 border-cream-300">
            {[
              { mode: 'By Air', info: 'Bhuntar Airport (Kullu–Manali) — 90 min by road. Transfers available.' },
              { mode: 'By Road', info: '250 km from Chandigarh via NH-3. The Beas valley drive is part of the experience.' },
              { mode: 'By Bus', info: 'HRTC daily services from Chandigarh & Manali direct to Sojha.' },
            ].map(item => (
              <div key={item.mode} className="px-5 py-4">
                <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-ink mb-1">{item.mode}</p>
                <p className="text-xs text-ink-soft font-light leading-relaxed">{item.info}</p>
              </div>
            ))}
          </div>
        </FadeIn>

      </div>
    </section>
  )
}

// ─── Reserve CTA ─────────────────────────────────────────────────────────────

function Reserve() {
  return (
    <section className="relative overflow-hidden py-48">
      <img src="/images/optimized/IMG_5169.webp" alt="Scenic Solitude" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-forest-950/60" />
      {/* Spotlight behind text */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,0,0,0.55) 0%, transparent 100%)' }} />

      <FadeIn className="relative z-10 wrap text-center">
        <span className="label-aman text-white/80">8 Accommodations · 2 Treehouses · 6 Cabins</span>
        <h2 className="text-5xl font-semibold text-white lg:text-7xl">Reserve<br />Your Stay</h2>
        <p className="mx-auto mt-8 max-w-md text-sm font-light text-white/85 leading-relaxed">
          Enquire and we will respond within 24 hours to confirm availability and discuss your visit.
        </p>
        <div className="mt-12 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <Link to="/inquire" className="btn-ghost-aman">
            Reserve
          </Link>
          <Link to="/contact" className="text-[11px] tracking-widest2 uppercase text-white/50 hover:text-white transition-colors">
            Contact Us
          </Link>
        </div>
      </FadeIn>
    </section>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Home() {
  return (
    <>
      <Hero />
      <About />
      <Treehouses />
      <Restaurant />
      <Experiences />
      <Gallery />
      <Testimonials />
      <Location />
      <Reserve />
    </>
  )
}
